import { useState, useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { labelsApi, type Label } from '@/api/labels';
import { accountsApi, type Account } from '@/api/accounts';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import {
  Tags,
  Search,
  Database,
  Filter,
  Trash2,
  CheckSquare,
  Square,
  MinusSquare,
  Download,
  Upload,
  X,
  FileSpreadsheet,
  EyeOff,
} from 'lucide-react';

export default function LabelManagement() {
  const { accessibleCompanies } = useAuthStore();
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [labels, setLabels] = useState<Label[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showInactive, setShowInactive] = useState(true);
  const [bulkPattern, setBulkPattern] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  // エクスポート関連
  const [excludedFromExport, setExcludedFromExport] = useState<Set<number>>(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [spreadsheetUrl, setSpreadsheetUrl] = useState('');
  const [sheetName, setSheetName] = useState('ラベル一覧');
  const [isExporting, setIsExporting] = useState(false);
  // インポート関連
  const [showImportModal, setShowImportModal] = useState(false);
  const [importSpreadsheetUrl, setImportSpreadsheetUrl] = useState('');
  const [importSheetName, setImportSheetName] = useState('');
  const [importCompanyId, setImportCompanyId] = useState<number | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  // 初期法人選択
  useEffect(() => {
    if (accessibleCompanies.length > 0 && !selectedCompanyId) {
      setSelectedCompanyId(accessibleCompanies[0].id);
    }
  }, [accessibleCompanies, selectedCompanyId]);

  // 法人変更時にアカウント取得
  useEffect(() => {
    if (selectedCompanyId) {
      fetchAccounts();
    }
  }, [selectedCompanyId]);

  // アカウント・フィルター変更時にラベル取得
  useEffect(() => {
    if (selectedCompanyId) {
      fetchLabels();
    }
  }, [selectedCompanyId, selectedAccountId, showInactive]);

  const fetchAccounts = async () => {
    if (!selectedCompanyId) return;
    try {
      const response = await accountsApi.getAccounts({
        company_id: selectedCompanyId,
        per_page: 100,
      });
      setAccounts(response.items);
    } catch (err) {
      console.error('アカウント取得エラー:', err);
    }
  };

  const fetchLabels = async () => {
    if (!selectedCompanyId) return;
    setIsLoading(true);
    try {
      const response = await labelsApi.getLabels({
        company_id: selectedCompanyId,
        account_id: selectedAccountId || undefined,
        is_active: showInactive ? undefined : true,
      });
      setLabels(response.items);
    } catch (err) {
      console.error('ラベル取得エラー:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleActive = async (label: Label) => {
    try {
      await labelsApi.updateLabel(label.id, { is_active: !label.is_active });
      setMessage({
        type: 'success',
        text: `ラベル「${label.name}」を${label.is_active ? '無効化' : '有効化'}しました`,
      });
      fetchLabels();
    } catch (err) {
      setMessage({ type: 'error', text: '更新に失敗しました' });
    }
  };

  const handleDeleteLabel = async (label: Label) => {
    if (!confirm(`ラベル「${label.name}」と関連データ(${label.data_count}件)を削除しますか？`)) {
      return;
    }
    try {
      const response = await labelsApi.deleteLabel(label.id);
      setMessage({ type: 'success', text: response.message });
      fetchLabels();
    } catch (err) {
      setMessage({ type: 'error', text: '削除に失敗しました' });
    }
  };

  const handleBulkDisable = async () => {
    if (!bulkPattern || !selectedCompanyId) return;
    if (!confirm(`「${bulkPattern}」を含むすべてのラベルを無効化しますか？`)) {
      return;
    }
    try {
      const response = await labelsApi.bulkDisableLabels(selectedCompanyId, bulkPattern);
      setMessage({ type: 'success', text: response.message });
      setBulkPattern('');
      fetchLabels();
    } catch (err) {
      setMessage({ type: 'error', text: '一括無効化に失敗しました' });
    }
  };

  const handleBulkDelete = async () => {
    if (!bulkPattern || !selectedCompanyId) return;
    if (!confirm(`「${bulkPattern}」を含むすべてのラベルとデータを削除しますか？この操作は取り消せません。`)) {
      return;
    }
    try {
      const response = await labelsApi.bulkDeleteLabels(selectedCompanyId, bulkPattern);
      setMessage({ type: 'success', text: response.message });
      setBulkPattern('');
      fetchLabels();
    } catch (err) {
      setMessage({ type: 'error', text: '一括削除に失敗しました' });
    }
  };

  // チェックボックス選択
  const handleToggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === filteredLabels.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredLabels.map((l) => l.id)));
    }
  };

  // 選択したラベルを一括有効化
  const handleBulkEnableSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`選択した ${selectedIds.size} 件のラベルを有効化しますか？`)) return;
    try {
      const response = await labelsApi.bulkUpdateByIds(Array.from(selectedIds), true);
      setMessage({ type: 'success', text: response.message });
      setSelectedIds(new Set());
      fetchLabels();
    } catch (err) {
      setMessage({ type: 'error', text: '一括有効化に失敗しました' });
    }
  };

  // 選択したラベルを一括無効化
  const handleBulkDisableSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`選択した ${selectedIds.size} 件のラベルを無効化しますか？`)) return;
    try {
      const response = await labelsApi.bulkUpdateByIds(Array.from(selectedIds), false);
      setMessage({ type: 'success', text: response.message });
      setSelectedIds(new Set());
      fetchLabels();
    } catch (err) {
      setMessage({ type: 'error', text: '一括無効化に失敗しました' });
    }
  };

  // 選択したラベルを一括削除
  const handleBulkDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    const selectedLabels = filteredLabels.filter((l) => selectedIds.has(l.id));
    const totalDataCount = selectedLabels.reduce((sum, l) => sum + l.data_count, 0);
    if (!confirm(`選択した ${selectedIds.size} 件のラベルと関連データ（${totalDataCount.toLocaleString()}件）を削除しますか？この操作は取り消せません。`)) {
      return;
    }
    try {
      const response = await labelsApi.bulkDeleteByIds(Array.from(selectedIds));
      setMessage({ type: 'success', text: response.message });
      setSelectedIds(new Set());
      fetchLabels();
    } catch (err) {
      setMessage({ type: 'error', text: '一括削除に失敗しました' });
    }
  };

  // エクスポート除外トグル
  const handleToggleExportExclusion = (id: number) => {
    setExcludedFromExport((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Google Sheets エクスポート
  const handleExportToGoogleSheets = async () => {
    const exportLabels = filteredLabels.filter((l) => !excludedFromExport.has(l.id));
    if (exportLabels.length === 0) {
      setMessage({ type: 'error', text: 'エクスポートするラベルがありません' });
      return;
    }

    if (!spreadsheetUrl.trim()) {
      setMessage({ type: 'error', text: 'スプレッドシートURLを入力してください' });
      return;
    }

    if (!sheetName.trim()) {
      setMessage({ type: 'error', text: 'シート名を入力してください' });
      return;
    }

    setIsExporting(true);
    try {
      const response = await labelsApi.exportToGoogleSheets({
        spreadsheet_url: spreadsheetUrl.trim(),
        sheet_name: sheetName.trim(),
        label_ids: exportLabels.map((l) => l.id),
      });
      setMessage({ type: 'success', text: response.message });
      setShowExportModal(false);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'エクスポートに失敗しました';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsExporting(false);
    }
  };

  // 全ラベルをエクスポート対象に戻す
  const handleClearExclusions = () => {
    setExcludedFromExport(new Set());
  };

  // Google Sheets インポート
  const handleImportFromGoogleSheets = async () => {
    if (!importSpreadsheetUrl.trim()) {
      setMessage({ type: 'error', text: 'スプレッドシートURLを入力してください' });
      return;
    }

    if (!importSheetName.trim()) {
      setMessage({ type: 'error', text: 'シート名を入力してください' });
      return;
    }

    if (!importCompanyId) {
      setMessage({ type: 'error', text: 'インポート先の法人を選択してください' });
      return;
    }

    setIsImporting(true);
    try {
      const response = await labelsApi.importFromGoogleSheets({
        spreadsheet_url: importSpreadsheetUrl.trim(),
        sheet_name: importSheetName.trim(),
        company_id: importCompanyId,
      });
      setMessage({ type: 'success', text: response.message });
      setShowImportModal(false);
      fetchLabels(); // ラベル一覧を再読み込み
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'インポートに失敗しました';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsImporting(false);
    }
  };

  // フィルタリング
  const filteredLabels = labels.filter((label) =>
    label.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    label.account_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const companyOptions = accessibleCompanies.map((c) => ({
    value: c.id,
    label: c.display_name,
  }));

  const accountOptions = [
    { value: '', label: 'すべての取得先' },
    ...accounts.map((a) => ({ value: a.id.toString(), label: a.name })),
  ];

  // 統計
  const activeCount = labels.filter((l) => l.is_active).length;
  const inactiveCount = labels.filter((l) => !l.is_active).length;
  const totalDataCount = labels.reduce((sum, l) => sum + l.data_count, 0);

  return (
    <div className="space-y-4">
      {/* ヘッダー */}
      <div>
        <h1 className="text-xl font-bold text-gray-900">ラベル管理</h1>
        <p className="text-sm text-gray-500">
          取得したラベルの有効/無効化やデータ削除を管理
        </p>
      </div>

      {/* メッセージ */}
      {message && (
        <div
          className={`p-3 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
        >
          {message.text}
          <button
            onClick={() => setMessage(null)}
            className="ml-2 text-sm underline"
          >
            閉じる
          </button>
        </div>
      )}

      {/* フィルター */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-48">
              {companyOptions.length > 1 ? (
                <Select
                  label="法人"
                  options={companyOptions}
                  value={selectedCompanyId?.toString() || ''}
                  onChange={(value) => setSelectedCompanyId(Number(value))}
                />
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">法人</label>
                  <div className="px-3 py-2 bg-gray-100 rounded-lg text-sm font-medium text-gray-900">
                    {companyOptions[0]?.label || '-'}
                  </div>
                </div>
              )}
            </div>

            <div className="w-56">
              <Select
                label="取得先ページ"
                options={accountOptions}
                value={selectedAccountId?.toString() || ''}
                onChange={(value) => setSelectedAccountId(value ? Number(value) : null)}
              />
            </div>

            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                検索
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="ラベル名で検索..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">無効も表示</span>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* 一括操作 */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="w-4 h-4" />
            一括操作
          </CardTitle>
        </CardHeader>
        <CardContent className="py-3">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                パターン（部分一致）
              </label>
              <input
                type="text"
                placeholder="例: テスト, 未分類"
                value={bulkPattern}
                onChange={(e) => setBulkPattern(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleBulkDisable}
              disabled={!bulkPattern}
            >
              一括無効化
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleBulkDelete}
              disabled={!bulkPattern}
            >
              一括削除
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 統計 */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="py-4 text-center">
            <div className="text-2xl font-bold text-green-600">{activeCount}</div>
            <div className="text-sm text-gray-500">有効ラベル</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4 text-center">
            <div className="text-2xl font-bold text-gray-400">{inactiveCount}</div>
            <div className="text-sm text-gray-500">無効ラベル</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{totalDataCount.toLocaleString()}</div>
            <div className="text-sm text-gray-500">データ件数</div>
          </CardContent>
        </Card>
      </div>

      {/* ラベル一覧 */}
      <Card>
        <CardHeader className="py-3 border-b">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Tags className="w-5 h-5" />
              ラベル一覧
            </CardTitle>
            <div className="flex items-center gap-4">
              {selectedIds.size > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-blue-600 font-medium">
                    {selectedIds.size}件選択中
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBulkEnableSelected}
                  >
                    有効化
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBulkDisableSelected}
                  >
                    無効化
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleBulkDeleteSelected}
                  >
                    削除
                  </Button>
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowImportModal(true)}
                className="flex items-center gap-1"
              >
                <Upload className="w-4 h-4" />
                インポート
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowExportModal(true)}
                className="flex items-center gap-1"
              >
                <Download className="w-4 h-4" />
                エクスポート
              </Button>
              <span className="text-sm text-gray-500">{filteredLabels.length}件</span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : filteredLabels.length > 0 ? (
            <div className="divide-y max-h-[500px] overflow-y-auto">
              {/* ヘッダー行（全選択チェックボックス） */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 border-b sticky top-0">
                <button
                  onClick={handleSelectAll}
                  className="p-1 hover:bg-gray-200 rounded"
                  title={selectedIds.size === filteredLabels.length ? '全選択解除' : '全選択'}
                >
                  {selectedIds.size === 0 ? (
                    <Square className="w-5 h-5 text-gray-400" />
                  ) : selectedIds.size === filteredLabels.length ? (
                    <CheckSquare className="w-5 h-5 text-blue-600" />
                  ) : (
                    <MinusSquare className="w-5 h-5 text-blue-600" />
                  )}
                </button>
                <span className="text-sm text-gray-600">
                  {selectedIds.size === 0
                    ? '全選択'
                    : selectedIds.size === filteredLabels.length
                    ? '全選択解除'
                    : `${selectedIds.size}件選択中`}
                </span>
              </div>
              {filteredLabels.map((label) => (
                <div
                  key={label.id}
                  className={`flex items-center justify-between p-4 hover:bg-gray-50 ${
                    !label.is_active ? 'bg-gray-50/50' : ''
                  } ${selectedIds.has(label.id) ? 'bg-blue-50' : ''}`}
                >
                  {/* チェックボックス */}
                  <button
                    onClick={() => handleToggleSelect(label.id)}
                    className="p-1 mr-3 hover:bg-gray-200 rounded"
                  >
                    {selectedIds.has(label.id) ? (
                      <CheckSquare className="w-5 h-5 text-blue-600" />
                    ) : (
                      <Square className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className={`font-medium ${
                          label.is_active ? 'text-gray-900' : 'text-gray-400 line-through'
                        }`}
                      >
                        {label.name}
                      </span>
                      {!label.is_active && (
                        <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded">
                          無効
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Database className="w-3 h-3" />
                        {label.account_name}
                      </span>
                      <span>{label.data_count.toLocaleString()}件のデータ</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    {/* エクスポート除外トグル */}
                    <button
                      onClick={() => handleToggleExportExclusion(label.id)}
                      className={`p-2 rounded-lg transition-colors ${
                        excludedFromExport.has(label.id)
                          ? 'text-orange-600 bg-orange-50 hover:bg-orange-100'
                          : 'text-gray-400 hover:bg-gray-100'
                      }`}
                      title={excludedFromExport.has(label.id) ? 'エクスポート対象に戻す' : 'エクスポートから除外'}
                    >
                      {excludedFromExport.has(label.id) ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <FileSpreadsheet className="w-4 h-4" />
                      )}
                    </button>
                    {/* トグルスイッチ */}
                    <button
                      onClick={() => handleToggleActive(label)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        label.is_active ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                      title={label.is_active ? '無効にする' : '有効にする'}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          label.is_active ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <button
                      onClick={() => handleDeleteLabel(label)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="削除"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Tags className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>ラベルがありません</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* エクスポートモーダル */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-green-600" />
                Googleスプレッドシートにエクスポート
              </h3>
              <button
                onClick={() => setShowExportModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
                disabled={isExporting}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {/* スプレッドシートURL入力 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  スプレッドシートURL <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  value={spreadsheetUrl}
                  onChange={(e) => setSpreadsheetUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  disabled={isExporting}
                />
                <p className="mt-1 text-xs text-gray-500">
                  サービスアカウントに編集権限を付与したスプレッドシートのURLを入力
                </p>
              </div>

              {/* シート名入力 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  シート（タブ）名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="ラベル一覧"
                  value={sheetName}
                  onChange={(e) => setSheetName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  disabled={isExporting}
                />
                <p className="mt-1 text-xs text-gray-500">
                  出力先のシート名（存在しない場合は自動作成）
                </p>
              </div>

              {/* エクスポート対象 */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-blue-700">
                  <FileSpreadsheet className="w-4 h-4" />
                  <span className="font-medium">エクスポート対象</span>
                </div>
                <p className="mt-1 text-sm text-blue-600">
                  {filteredLabels.length - excludedFromExport.size}件のラベル
                  {excludedFromExport.size > 0 && (
                    <span className="text-orange-600 ml-1">
                      （{excludedFromExport.size}件は除外）
                    </span>
                  )}
                </p>
              </div>

              {excludedFromExport.size > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-orange-700">
                      <EyeOff className="w-4 h-4" />
                      <span className="font-medium">除外中のラベル</span>
                    </div>
                    <button
                      onClick={handleClearExclusions}
                      className="text-xs text-orange-600 hover:text-orange-800 underline"
                      disabled={isExporting}
                    >
                      すべて解除
                    </button>
                  </div>
                  <div className="mt-2 max-h-32 overflow-y-auto">
                    {filteredLabels
                      .filter((l) => excludedFromExport.has(l.id))
                      .map((label) => (
                        <div
                          key={label.id}
                          className="flex items-center justify-between text-sm py-1"
                        >
                          <span className="text-gray-700 truncate">{label.name}</span>
                          <button
                            onClick={() => handleToggleExportExclusion(label.id)}
                            className="text-orange-600 hover:text-orange-800 text-xs ml-2"
                            disabled={isExporting}
                          >
                            解除
                          </button>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                <p className="flex items-center gap-2">
                  <EyeOff className="w-4 h-4 text-orange-500" />
                  除外したいラベルは、一覧の
                  <FileSpreadsheet className="w-4 h-4" />
                  アイコンをクリックして除外できます
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
              <Button
                variant="outline"
                onClick={() => setShowExportModal(false)}
                disabled={isExporting}
              >
                キャンセル
              </Button>
              <Button
                variant="primary"
                onClick={handleExportToGoogleSheets}
                disabled={
                  filteredLabels.length - excludedFromExport.size === 0 ||
                  !spreadsheetUrl.trim() ||
                  !sheetName.trim() ||
                  isExporting
                }
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
              >
                {isExporting ? (
                  <>
                    <LoadingSpinner />
                    エクスポート中...
                  </>
                ) : (
                  <>
                    <FileSpreadsheet className="w-4 h-4" />
                    スプレッドシートに出力
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* インポートモーダル */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Upload className="w-5 h-5 text-blue-600" />
                Googleスプレッドシートからインポート
              </h3>
              <button
                onClick={() => setShowImportModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
                disabled={isImporting}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {/* スプレッドシートURL入力 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  スプレッドシートURL <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  value={importSpreadsheetUrl}
                  onChange={(e) => setImportSpreadsheetUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isImporting}
                />
                <p className="mt-1 text-xs text-gray-500">
                  サービスアカウントに閲覧権限を付与したスプレッドシートのURLを入力
                </p>
              </div>

              {/* シートのタブ名入力 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  シートのタブ名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="例: Sheet1"
                  value={importSheetName}
                  onChange={(e) => setImportSheetName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isImporting}
                />
                <p className="mt-1 text-xs text-gray-500">
                  読み込むシートのタブ名（A列: タグ名, B列: ラベル名）
                </p>
              </div>

              {/* インポート先法人選択 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  インポート先の法人 <span className="text-red-500">*</span>
                </label>
                <select
                  value={importCompanyId || ''}
                  onChange={(e) => setImportCompanyId(e.target.value ? Number(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isImporting}
                >
                  <option value="">法人を選択してください</option>
                  {accessibleCompanies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.display_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-blue-700">
                  <FileSpreadsheet className="w-4 h-4" />
                  <span className="font-medium">インポート形式</span>
                </div>
                <ul className="mt-1 text-sm text-blue-600 list-disc list-inside space-y-1">
                  <li>A列にラベル名が記載されたスプレッドシートを読み込みます</li>
                  <li>1行目はヘッダーとしてスキップされます</li>
                  <li>シート名がラベルのタグ（カテゴリ）として設定されます</li>
                </ul>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-700">
                <p>既に登録されているラベル名は自動的にスキップされます。</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
              <Button
                variant="outline"
                onClick={() => setShowImportModal(false)}
                disabled={isImporting}
              >
                キャンセル
              </Button>
              <Button
                variant="primary"
                onClick={handleImportFromGoogleSheets}
                disabled={
                  !importSpreadsheetUrl.trim() ||
                  !importSheetName.trim() ||
                  !importCompanyId ||
                  isImporting
                }
                className="flex items-center gap-2"
              >
                {isImporting ? (
                  <>
                    <LoadingSpinner />
                    インポート中...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    インポート実行
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
